/**
 * "Space weather" in Teslyn Cloud (R115; help `cloud.space-weather`; docs/specs/space-weather.md §4, §6): for staff on
 * their phones. What space weather means for flying today against the chosen project's own Diurnal QC limits
 * (decisions 742, 749, 750, 754 to 757), the indices now, alerts and warnings, the next three days, the solar wind,
 * CMEs and WSA-Enlil (Play only when pressed, decision 690) and the reading list. The same domain logic and the same
 * provider proxy as Teslyn Field's screen (`space-weather-view.ts`); the observatory trace stays in Teslyn Field
 * (decision 340: a staff guide on the laptop). Staff only (decisions 688, 691): no client role has the page and the
 * proxy refuses clients. Every value is shown as text; links open only on the sources' own sites.
 */
import { useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  SOLAR_WIND_MEAN_MINUTES,
  SPACE_WEATHER_LINKS,
  ageState,
  ageWords,
  buildModel,
  centreWords,
  dayAndUtc,
  daylightBands,
  gScaleWords,
  kWords,
  l1LeadMinutes,
  latestDensity,
  likelyKpFor,
  newest,
  noaaCodeMeaning,
  spaceWeatherProjectContext,
  spaceWeatherRequests,
  spaceWeatherSource,
  spacecraftName,
  swDay,
  swLink,
  utcAndLocal,
  type Block,
  type KeyValue,
  type Part,
  type ProxyClient,
  type SpaceWeatherSourceId,
  type SwModel,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import {
  loadProjectBlocks,
  loadSpaceWeatherProjects,
  webEnlilSource,
  webProxyClient,
  type SwProject,
} from './api.ts';
import { EnlilPlayer, type EnlilSource } from './EnlilPlayer.tsx';
import { FetchingBanner } from './FetchingBanner.tsx';
import { KpBars, TraceChart } from './charts.tsx';
import { browserStore, useSpaceWeatherData } from './useSpaceWeatherData.ts';

export interface SpaceWeatherScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  /** Tests: the clock, the provider proxy, the browser storage and the Enlil frames' source. */
  readonly now?: () => number;
  readonly client?: ProxyClient;
  readonly store?: KeyValue | null;
  readonly enlil?: EnlilSource;
}

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const kp = (v: number): string => v.toFixed(2);
const chosenKey = (tenantId: string, userId: string): string =>
  `teslyn.spaceweather.project.${tenantId}.${userId}`;

/** The page: a project to judge for (the one being flown first), then its space weather. */
export function SpaceWeatherScreen(props: SpaceWeatherScreenProps): ReactElement {
  const { sb, tenantId, userId } = props;
  const store = props.store === undefined ? browserStore() : props.store;
  const [projects, setProjects] = useState<readonly SwProject[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string | null>(() => {
    try {
      return store?.getItem(chosenKey(tenantId, userId)) ?? null;
    } catch {
      return null;
    }
  });
  const reload = (): void => {
    setError(null);
    loadSpaceWeatherProjects(sb, tenantId)
      .then(setProjects)
      .catch((e: unknown) => setError(message(e)));
  };
  useEffect(reload, [sb, tenantId]);
  const project = projects?.find((p) => p.id === chosen) ?? projects?.[0] ?? null;
  const choose = (id: string): void => {
    setChosen(id);
    try {
      store?.setItem(chosenKey(tenantId, userId), id);
    } catch {
      /* the choice is only for this visit */
    }
  };

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!projects) return <Loading what="projects" />;
  if (!project) {
    return (
      <section className="page" aria-label="Space weather">
        <PageHeader title="Space weather" />
        <p className="body">
          There is no project to judge space weather for yet. Space weather is judged against a project’s own
          diurnal limits and blocks.
        </p>
      </section>
    );
  }
  return (
    <ProjectSpaceWeather
      key={project.id}
      {...props}
      store={store}
      project={project}
      projects={projects}
      onChoose={choose}
    />
  );
}

function ProjectSpaceWeather({
  sb,
  tenantId,
  project,
  projects,
  onChoose,
  now = Date.now,
  client: givenClient,
  store,
  enlil: givenEnlil,
}: SpaceWeatherScreenProps & {
  readonly project: SwProject;
  readonly projects: readonly SwProject[];
  readonly onChoose: (id: string) => void;
}): ReactElement {
  const [blocks, setBlocks] = useState<readonly Block[] | null>(null);
  useEffect(() => {
    let live = true;
    loadProjectBlocks(sb, project.id)
      .then((b) => live && setBlocks(b))
      .catch(() => live && setBlocks([]));
    return () => {
      live = false;
    };
  }, [sb, project.id]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const nowMs = now();
  const today = new Date(nowMs).toISOString().slice(0, 10);
  // the project's own Diurnal QC limits as Diurnal QC resolves them; Teslyn Cloud has no personal values
  const ctx = useMemo(
    () => spaceWeatherProjectContext({ project, blocks, nowMs }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once a day and on new blocks
    [project, blocks, today],
  );
  const client = useMemo(() => givenClient ?? webProxyClient(sb, tenantId), [givenClient, sb, tenantId]);
  const enlilSource = useMemo(() => givenEnlil ?? webEnlilSource(sb), [givenEnlil, sb]);
  const requests = useMemo(() => spaceWeatherRequests(ctx.centre), [ctx.centre]);
  const data = useSpaceWeatherData({
    tenantId,
    client,
    requests,
    store: store ?? null,
    now,
  });
  const m: SwModel = useMemo(
    () => buildModel(data.entries, { ...ctx, nowMs, observatory: null }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- each minute (tick) and on new answers
    [data.entries, ctx, tick],
  );
  const zone = ctx.zoneLabel;
  const offsetH = ctx.offsetH;
  const lastSeen = newest(...Object.values(data.entries).map((e) => e.held?.fetchedAt ?? null));
  const o = m.outlook;
  const fc = m.next.forecast;
  const from = Date.parse(`${today}T00:00:00Z`);
  const wind = m.wind.now.value;
  const dayPts = m.wind.day.value?.points ?? [];
  const lead = l1LeadMinutes(wind?.speed ?? null);
  const density = latestDensity(dayPts);
  const tone = o.tone === 'muted' ? '' : o.tone;
  const at = (iso: string): string => utcAndLocal(iso, offsetH, zone);

  return (
    <section className="page sw" aria-label="Space weather">
      <PageHeader
        title="Space weather"
        subtitle={`${ctx.projectName}${ctx.centre ? `, blocks centre ${centreWords(ctx.centre)}` : ''}. Times in UTC with ${zone} beside them.`}
      >
        <Button busy={data.busy} disabled={data.busy} onClick={data.refresh}>
          Refresh
        </Button>
      </PageHeader>
      <label className="field sw-project">
        <span className="label">Project</span>
        <select className="input" value={project.id} onChange={(e) => onChoose(e.target.value)}>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {`${p.number} ${p.title}`.trim()}
            </option>
          ))}
        </select>
      </label>
      {/* R147, R168 (MB, 2 and 3 Nov 2026): the page asks its sources one at a time; say so, with the count and a bar */}
      <FetchingBanner progress={data.progress} rounds={data.rounds} />
      {data.offline && (
        <p className="notice notice--warn" role="status">
          No connection: showing what this browser last saw
          {lastSeen ? ` at ${at(lastSeen)}` : ''}. The Enlil player needs the internet.
        </p>
      )}

      <div className="sw-grid">
        <Section
          title="What this means for flying today"
          at={m.newestKFetch}
          now={nowMs}
          zone={zone}
          offsetH={offsetH}
          label="Verdict"
          lead
        >
          <div className={`sw-verdict${tone ? ` sw-verdict--${tone}` : ''}`} title={o.basis}>
            <span className={`chip sw-verdict__word${tone ? ` chip--${tone}` : ''}`}>{o.word}</span>
            <p className="body sw-verdict__sentence">
              {o.sentence}
              {o.level === 'unknown' && m.newestKFetch ? ` Last update ${at(m.newestKFetch)}.` : ''}
            </p>
          </div>
          {m.storm.length > 0 && (
            <div className="chips" aria-label="Storm notices">
              {m.storm.map((e) => (
                <span
                  key={e.eventKey}
                  className={e.kind === 'storm_now' ? 'chip chip--fail' : 'chip chip--warn'}
                >
                  {e.kind === 'storm_now' ? 'Storm now' : 'Storm watch'}
                </span>
              ))}
            </div>
          )}
          <ul className="sw-list caption">
            {o.reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          {o.expected.length > 0 && (
            <details className="sw-more">
              <summary className="caption">Days like this in Thomson’s history</summary>
              <ul className="sw-list caption">
                {o.expected.map((e) => (
                  <li key={e.limit.label}>{e.words}</li>
                ))}
              </ul>
            </details>
          )}
          {o.notes.map((n) => (
            <p key={n} className="caption muted">
              {n}
            </p>
          ))}
          <p className="caption muted">
            {o.limitsText} Levels set by MB; the days beside them are Thomson’s basemag history (Plot 1 and
            Plot 2 from MagStation and G-823 bases only). A guide for planning: the basemag and Diurnal QC
            decide.
          </p>
        </Section>

        <Section
          title="Now"
          at={newest(m.now.kAus.fetchedAt, m.now.kp1m.fetchedAt, m.now.scales.fetchedAt)}
          now={nowMs}
          zone={zone}
          offsetH={offsetH}
        >
          {m.now.kAus.value ? (
            <div
              className="sw-kstrip"
              role="img"
              aria-label={`Australian region K ${m.now.kAus.value.index} of 9, ${kWords(m.now.kAus.value.index)}`}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((k) => (
                <span
                  key={k}
                  className={
                    k === m.now.kAus.value!.index ? 'sw-kstrip__k sw-kstrip__k--on data' : 'sw-kstrip__k data'
                  }
                >
                  {k}
                </span>
              ))}
            </div>
          ) : (
            <Missing part={m.now.kAus} what="Australian K (BoM)" />
          )}
          <dl className="kv sw-kv">
            {m.now.kAus.value && (
              <Row name="Australian K (BoM)">
                {m.now.kAus.value.index} {kWords(m.now.kAus.value.index)}, from{' '}
                {dayAndUtc(m.now.kAus.value.validTime)}
              </Row>
            )}
            {m.now.nearest ? (
              <Row name={`${m.now.nearest.site} K (nearest, ${Math.round(m.now.nearest.km)} km)`}>
                {m.now.nearest.k.index}
                {m.now.nearest.passedOver.length
                  ? ` (${m.now.nearest.passedOver.join(', ')}: no recent K)`
                  : ''}
              </Row>
            ) : (
              m.now.nearestSiteName &&
              m.now.kAus.code !== 'not_set_up' && (
                <Row name={`${m.now.nearestSiteName} K (nearest)`}>no recent value</Row>
              )
            )}
            {m.now.kp1m.value && <Row name="Planetary Kp (estimate now)">{kp(m.now.kp1m.value.kp)}</Row>}
            {m.now.kp3h.value && (
              <Row name="Planetary Kp (3 hours)">
                {kp(m.now.kp3h.value.kp)} from {dayAndUtc(m.now.kp3h.value.t)}
              </Row>
            )}
            {m.now.scales.value && <Row name="NOAA storm level">{gScaleWords(m.now.scales.value.now.g)}</Row>}
            {m.now.dst.value && <Row name="AusDst">{m.now.dst.value.index} nT</Row>}
            {m.now.aIndex.value && (
              <Row name={`Australian A (${m.now.aIndex.value.validTime.slice(0, 10)})`}>
                {m.now.aIndex.value.index}
              </Row>
            )}
          </dl>
          {!m.now.scales.value && <Missing part={m.now.scales} what="NOAA scales" />}
          {m.now.pictures.value?.pc3Icon && (
            <div className="row">
              <span className="caption">pc3 index (BoM, largest of the last hour)</span>
              <img
                className="sw-icon"
                src={m.now.pictures.value.pc3Icon}
                alt="BoM Australian region pc3 pulsation index icon"
              />
            </div>
          )}
          <Credit source="bom_sws" extra="K, A, Dst and pc3 with BoM’s issue times above" />
          <Credit source="noaa_swpc" />
        </Section>

        <Section
          title="Alerts and warnings"
          at={newest(m.alerts.warning.fetchedAt, m.alerts.noaa.fetchedAt, m.now.pictures.fetchedAt)}
          now={nowMs}
          zone={zone}
          offsetH={offsetH}
        >
          {m.alerts.warning.code === 'not_set_up' ? (
            <p className="caption">{m.alerts.warning.error}</p>
          ) : (
            <dl className="kv sw-kv">
              <Row name="BoM geophysical warning">
                {m.alerts.warning.value
                  ? `Issued ${m.alerts.warning.value.issueTime ? dayAndUtc(m.alerts.warning.value.issueTime) : '?'}, ${m.alerts.warning.value.startDate} to ${m.alerts.warning.value.endDate}${m.alerts.warning.value.cause ? ` (${m.alerts.warning.value.cause})` : ''}`
                  : m.alerts.warning.fetchedAt
                    ? 'None'
                    : '…'}
              </Row>
              {m.alerts.warning.value?.activity.map((a) => (
                <Row key={a.date} name={swDay(a.date)}>
                  {a.forecast}
                </Row>
              ))}
              <Row name="BoM magnetic alert">
                {m.alerts.alert.value
                  ? `G${m.alerts.alert.value.gScale ?? '?'} ${m.alerts.alert.value.description ?? ''}${m.alerts.alert.value.validUntil ? `, valid until ${dayAndUtc(m.alerts.alert.value.validUntil)}` : ''}`
                  : m.alerts.alert.fetchedAt
                    ? 'None'
                    : '…'}
              </Row>
              {m.alerts.auroras.map((a, i) => (
                <Row key={i} name={`BoM aurora ${['alert', 'watch', 'outlook'][i]}`}>
                  {a.value
                    ? `K ${a.value.kAus ?? '?'}${a.value.latBand ? `, ${a.value.latBand} latitudes` : ''}${a.value.time ? `, ${dayAndUtc(a.value.time)}` : ''}`
                    : a.fetchedAt
                      ? 'None'
                      : '…'}
                </Row>
              ))}
              <Row name="BoM GEOSTAT alert">
                {m.now.pictures.value?.geostat ? m.now.pictures.value.geostat.text : '…'}
              </Row>
              {m.alerts.magnetic.length === 0 && (
                <Row name="NOAA geomagnetic messages (24 h)">{m.alerts.noaa.fetchedAt ? 'None' : '…'}</Row>
              )}
            </dl>
          )}
          {m.alerts.warning.value?.comments && (
            <p className="caption">Forecaster: {m.alerts.warning.value.comments}</p>
          )}
          {m.alerts.warning.code === 'not_set_up' && m.alerts.magnetic.length === 0 && (
            <p className="caption">
              NOAA geomagnetic messages (24 h): {m.alerts.noaa.fetchedAt ? 'none' : '…'}
            </p>
          )}
          {m.alerts.magnetic.map((msg) => (
            <details key={`${msg.productId}-${msg.serial ?? msg.issued}`} className="sw-more">
              <summary className="caption">
                <span className="data">{msg.productId}</span> {noaaCodeMeaning(msg.productId)}: issued{' '}
                {dayAndUtc(msg.issued)}
                {msg.validTo ? `, valid to ${dayAndUtc(msg.validTo)}` : ''}
              </summary>
              <pre className="data sw-message">{msg.text}</pre>
            </details>
          ))}
          {m.alerts.others.length > 0 && (
            <details className="sw-more">
              <summary className="caption muted">Not about magnetics ({m.alerts.others.length})</summary>
              {m.alerts.others.map((msg) => (
                <p key={`${msg.productId}-${msg.serial ?? msg.issued}`} className="caption">
                  <span className="data">{msg.productId}</span> {msg.title}, {dayAndUtc(msg.issued)}
                </p>
              ))}
            </details>
          )}
          <Credit source="bom_sws" />
          <Credit source="noaa_swpc" />
        </Section>

        <Section title="Next 3 days" at={m.next.fetchedAt} now={nowMs} zone={zone} offsetH={offsetH} wide>
          {fc ? (
            <>
              <p className="caption">
                NOAA {fc.product}, issued {dayAndUtc(fc.issued)} (the newer of NOAA’s two Kp forecasts).
                Dashed line: Kp {likelyKpFor(o)}, where “Exceedances likely” starts for this project’s limits;
                shaded: daylight at the blocks.
              </p>
              <KpBars
                bins={fc.kp}
                likelyKp={likelyKpFor(o)}
                daylight={daylightBands(ctx.centre, from)}
                fromMs={from}
                nowMs={nowMs}
              />
              <ul className="sw-days">
                {m.next.watchByDay.map((w, i) => {
                  const p = m.next.geomag?.probabilities.find((x) => x.date === w.date);
                  const g = m.now.scales.value?.days[i]?.g ?? null;
                  return (
                    <li key={w.date} className="stack sw-day">
                      <span className="label">{swDay(w.date)}</span>
                      {p && (
                        <span className="caption">
                          Active {p.active}%, minor storm {p.minor}%, moderate {p.moderate}%, strong{' '}
                          {p.strong}%
                        </span>
                      )}
                      <span className="caption">
                        NOAA watch: {w.g === null ? 'none' : w.g === 0 ? 'none (below G1)' : `G${w.g}`}
                        {g !== null ? `; expected ${gScaleWords(g)}` : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {fc.rationale && <p className="caption">NOAA: {fc.rationale}</p>}
              {m.next.daylight && (
                <p className="caption">
                  {m.next.daylight.tomorrow ? 'Tomorrow’s' : 'The rest of today’s'} daylight (
                  {at(new Date(m.next.daylight.from).toISOString())} to{' '}
                  {at(new Date(m.next.daylight.to).toISOString())}
                  ): Kp at most {kp(m.next.daylight.kp)}.
                </p>
              )}
            </>
          ) : (
            <p className="caption muted">{m.next.error ?? 'Waiting for NOAA’s forecast.'}</p>
          )}
          {m.alerts.warning.value && (
            <p className="caption">
              BoM warning:{' '}
              {m.alerts.warning.value.activity.map((a) => `${swDay(a.date)} ${a.forecast}`).join('; ')}
            </p>
          )}
          <Credit source="noaa_swpc" />
        </Section>

        <Section
          title={`Solar wind at L1${m.wind.day.value?.source ? ` (${spacecraftName(m.wind.day.value.source)})` : ''}`}
          at={newest(m.wind.now.fetchedAt, m.wind.day.fetchedAt)}
          now={nowMs}
          zone={zone}
          offsetH={offsetH}
          label="Solar wind"
        >
          {wind ? (
            <dl className="sw-wind">
              <div>
                <dt className="caption muted">Speed</dt>
                <dd className="data">{wind.speed ?? '–'} km/s</dd>
              </div>
              <div>
                <dt className="caption muted">Density</dt>
                <dd className="data">{density ?? '–'} p/cm³</dd>
              </div>
              <div>
                <dt className="caption muted">Bt</dt>
                <dd className="data">{wind.bt ?? '–'} nT</dd>
              </div>
              <div>
                <dt className="caption muted">Bz</dt>
                <dd className="data">{wind.bz === null ? '–' : `${wind.bz > 0 ? '+' : ''}${wind.bz}`} nT</dd>
              </div>
            </dl>
          ) : (
            <Missing part={m.wind.now} what="Solar wind" />
          )}
          {wind?.magTime && (
            <p className="caption muted">
              Measured {dayAndUtc(wind.magTime)}
              {lead ? `; reaches Earth about ${lead} minutes later.` : '.'}
            </p>
          )}
          {dayPts.length > 0 && (
            <>
              <TraceChart
                label="Speed, 24 h"
                unit="km/s"
                points={dayPts.map((p) => ({ t: Date.parse(p.t), v: p.speed }))}
                fromMs={nowMs - 86_400_000}
                toMs={nowMs}
              />
              <TraceChart
                label="Density, 24 h"
                unit="p/cm³"
                points={dayPts.map((p) => ({ t: Date.parse(p.t), v: p.density }))}
                fromMs={nowMs - 86_400_000}
                toMs={nowMs}
              />
              <TraceChart
                label="Bz GSM and Bt, 24 h (Bz south, below 0, drives storms)"
                unit="nT"
                zero
                points={dayPts.map((p) => ({ t: Date.parse(p.t), v: p.bz }))}
                points2={dayPts.map((p) => ({ t: Date.parse(p.t), v: p.bt }))}
                names={['Bz', 'Bt']}
                fromMs={nowMs - 86_400_000}
                toMs={nowMs}
              />
              <p className="caption muted">
                {SOLAR_WIND_MEAN_MINUTES}-minute means of the spacecraft NOAA marks active. Times in UTC.
              </p>
            </>
          )}
          {m.wind.swcom.value && (
            <>
              <p className="caption">
                Spaceweather.com: speed {m.wind.swcom.value.speed_km_s ?? '–'} km/s, density{' '}
                {m.wind.swcom.value.density_cm3 ?? '–'} protons/cm³
                {m.wind.swcom.value.updated_text ? ` (${m.wind.swcom.value.updated_text})` : ''}.
              </p>
              <Credit source="spaceweather_com" extra="Its numbers come from NOAA’s real-time solar wind" />
            </>
          )}
          <Credit source="noaa_swpc" />
        </Section>

        <Section
          title="CMEs heading for Earth"
          at={newest(m.cme.arrivals.fetchedAt, m.cme.frames.fetchedAt)}
          now={nowMs}
          zone={zone}
          offsetH={offsetH}
          label="CMEs"
        >
          {m.cme.outcomes.length === 0 ? (
            <p className="body">
              {m.cme.arrivals.fetchedAt
                ? 'No Earth-directed CME expected in DONKI’s model runs of the last 7 days.'
                : 'Waiting for DONKI.'}
            </p>
          ) : (
            m.cme.outcomes.map(({ arrival: a, observedKp, passed, dayAfter }) => (
              <div key={a.simulationId} className="stack">
                <p className="body">
                  {passed ? 'Forecast arrival' : 'Possible arrival'} {dayAndUtc(a.arrival)} (
                  {a.glancing ? 'glancing blow' : a.minorImpact ? 'minor impact' : 'impact'}, model estimate
                  {a.kp90 !== null
                    ? `, Kp ${a.kp90}${a.kpMax !== null && a.kpMax !== a.kp90 ? ` to ${a.kpMax}` : ''}`
                    : ''}
                  ){a.cmeSpeed ? `; CME ${a.cmeSpeed} km/s` : ''}.
                </p>
                {observedKp !== null && (
                  <p className="caption">
                    What happened: observed Kp at most {kp(observedKp)}{' '}
                    {dayAfter ? 'in the day after' : 'since then'}.
                  </p>
                )}
              </div>
            ))
          )}
          <EnlilPlayer frames={m.cme.frames.value ?? []} latest={m.cme.latest} source={enlilSource} />
          <Credit source="nasa_donki" extra="Arrival times are model estimates" />
        </Section>

        <Section title="Reading" at={m.reading.fetchedAt} now={nowMs} zone={zone} offsetH={offsetH} wide>
          <ul className="sw-reading">
            {m.reading.items.map((it) => {
              const href = swLink(it.url);
              return (
                <li key={`${it.source}:${it.url}:${it.title}`} className="sw-reading__item">
                  <span className="label">{spaceWeatherSource(it.source).short}</span>
                  <span className="caption muted">{dayAndUtc(it.date).replace(' 00:00 UTC', '')}</span>
                  {href ? (
                    <a
                      className="caption link sw-reading__title"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {it.title}
                    </a>
                  ) : (
                    <span className="caption sw-reading__title">{it.title}</span>
                  )}
                  {it.summary && <span className="caption muted sw-reading__summary">{it.summary}</span>}
                </li>
              );
            })}
          </ul>
          {m.reading.credit && <Credit source="spaceweather_com" extra={m.reading.credit} />}
          <p className="caption sw-sources">
            Also:{' '}
            {SPACE_WEATHER_LINKS.map((l, i) => (
              <span key={l.url}>
                {i > 0 ? ', ' : ''}
                <a
                  className="link"
                  href={swLink(l.url) ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {l.label}
                </a>
              </span>
            ))}
            . Links open the source’s own site.
          </p>
        </Section>
      </div>
      <p className="caption muted sw-footer">
        Data: BoM Space Weather Services (ASWFC), presented with the Bureau’s permission; NOAA SWPC; NASA CCMC
        DONKI; Spaceweather.com. A guide for planning, not an official briefing and not a QC result: the
        basemag and Diurnal QC decide. The nearest observatory’s trace is on Teslyn Field’s Space weather
        screen.
      </p>
    </section>
  );
}

/** "Updated 05:45 UTC (13:45 UTC+8), 3 min ago"; over 30 minutes "Old: …" in the warn colour. */
function Age({
  at,
  now,
  offsetH,
  zone,
}: {
  readonly at: string | null;
  readonly now: number;
  readonly offsetH: number;
  readonly zone: string;
}): ReactElement {
  if (!at) return <span className="caption muted">Not fetched yet</span>;
  const when = utcAndLocal(at, offsetH, zone);
  return ageState(at, now) === 'fresh' ? (
    <span className="caption muted">
      Updated {when}, {ageWords(at, now)}
    </span>
  ) : (
    <span className="caption sw-old">
      Old: {ageWords(at, now).replace(/ ago$/, '')} (updated {when})
    </span>
  );
}

function Section({
  title,
  at,
  now,
  offsetH,
  zone,
  children,
  wide = false,
  lead = false,
  label,
}: {
  readonly title: string;
  readonly at: string | null;
  readonly now: number;
  readonly offsetH: number;
  readonly zone: string;
  readonly children: ReactNode;
  readonly wide?: boolean;
  /** The verdict: first, and across the whole width. */
  readonly lead?: boolean;
  readonly label?: string;
}): ReactElement {
  const cls = ['panel', 'stack', 'sw-section', wide || lead ? 'sw-section--wide' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <article className={cls} aria-label={label ?? title}>
      <header className="sw-section__head">
        <h2 className="heading">{title}</h2>
        <Age at={at} now={now} offsetH={offsetH} zone={zone} />
      </header>
      <div className={at && ageState(at, now) === 'unknown' ? 'stack sw-stale' : 'stack'}>{children}</div>
    </article>
  );
}

function Row({ name, children }: { readonly name: string; readonly children: ReactNode }): ReactElement {
  return (
    <>
      <dt className="caption">{name}</dt>
      <dd className="data">{children}</dd>
    </>
  );
}

function Missing<T>({ part, what }: { readonly part: Part<T>; readonly what: string }): ReactElement | null {
  if (part.value !== null) return null;
  if (part.error) return <p className="caption muted">{`${what}: ${part.error}`}</p>;
  return part.fetchedAt ? null : <p className="caption muted">{what}: waiting for the first answer.</p>;
}

/** A credit line with the source's own site (BoM's conditions: name, issue time, link; decisions 688, 689). */
function Credit({
  source,
  extra,
}: {
  readonly source: SpaceWeatherSourceId;
  readonly extra?: string;
}): ReactElement {
  const s = spaceWeatherSource(source);
  const href = swLink(s.home);
  return (
    <p className="caption muted sw-sources">
      Source:{' '}
      {href ? (
        <a className="link" href={href} target="_blank" rel="noopener noreferrer">
          {s.credit}
        </a>
      ) : (
        s.credit
      )}
      {extra ? `. ${extra}` : ''}
    </p>
  );
}
